(() => {
  'use strict';

  const WISHLIST_KEY = 'shadoraWishlistV1';
  const RECENT_KEY = 'shadoraRecentlyViewedV1';
  const MAX_ITEMS = 50;
  const MAX_REQUEST_PHOTO_BYTES = 5 * 1024 * 1024;
  const ALLOWED_REQUEST_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  const REQUEST_STATUS = {
    received: { label: 'Talebiniz Alındı', className: 'received' },
    reviewing: { label: 'İnceleniyor', className: 'reviewing' },
    sourcing: { label: 'Tedarik Araştırılıyor', className: 'sourcing' },
    published: { label: 'Siteye Yüklendi', className: 'published' },
    unavailable: { label: 'Şu Anda Temin Edilemiyor', className: 'unavailable' }
  };

  const ORDER_NOTE_PRESETS = {
    new_address: 'Teslimat adresimi değiştirmek istiyorum. Lütfen kargolamadan önce benimle iletişime geçin.',
    special_request: 'Siparişimle ilgili özel bir isteğim bulunuyor. Lütfen benimle iletişime geçin.',
    contact_before_shipping: 'Lütfen siparişimi kargolamadan önce benimle iletişime geçin.',
    custom: ''
  };

  let myRequestsLoaded = false;
  let adminRequestsLoaded = false;
  let adminRequestItems = [];
  let requestPhotoObjectUrl = '';

  const safeParse = (value) => {
    try {
      const parsed = JSON.parse(value || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  };

  const read = (key) => safeParse(localStorage.getItem(key));
  const write = (key, items) => localStorage.setItem(key, JSON.stringify(items.slice(0, MAX_ITEMS)));

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const escapeAttribute = (value = '') => escapeHtml(value).replace(/`/g, '&#096;');

  const productFromButton = (button) => ({
    handle: button.dataset.productHandle || '',
    title: button.dataset.productTitle || '',
    url: button.dataset.productUrl || '',
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || ''
  });

  const isSaved = (handle) => read(WISHLIST_KEY).some((item) => item.handle === handle);

  const setButtonState = (button, saved) => {
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    const label = button.querySelector('.shadora-wishlist-button__label');
    if (label) label.textContent = saved ? 'Favorilerden çıkar' : 'Favorilere ekle';
    const hiddenLabel = button.querySelector('.visually-hidden');
    if (hiddenLabel) hiddenLabel.textContent = saved ? 'Favorilerden çıkar' : 'Favorilere ekle';
  };

  const syncButtons = () => {
    document.querySelectorAll('[data-shadora-wishlist-button]').forEach((button) => {
      setButtonState(button, isSaved(button.dataset.productHandle));
    });
  };

  const toggleWishlist = (product) => {
    if (!product.handle) return;
    const items = read(WISHLIST_KEY);
    const existing = items.findIndex((item) => item.handle === product.handle);
    if (existing >= 0) items.splice(existing, 1);
    else items.unshift(product);
    write(WISHLIST_KEY, items);
    syncButtons();
    renderSavedProducts();
  };

  const saveRecent = (product) => {
    if (!product.handle) return;
    const items = read(RECENT_KEY).filter((item) => item.handle !== product.handle);
    items.unshift(product);
    write(RECENT_KEY, items);
  };

  const productCardHtml = (product, removable) => {
    const title = escapeHtml(product.title);
    const url = escapeAttribute(product.url || '#');
    const image = escapeAttribute(product.image);
    const price = escapeHtml(product.price);
    const handle = escapeAttribute(product.handle);

    return `
      <article class="shadora-saved-card">
        ${removable ? `<button type="button" class="shadora-saved-card__remove" data-remove-wishlist="${handle}" aria-label="${title} ürününü favorilerden çıkar">×</button>` : ''}
        <a class="shadora-saved-card__image" href="${url}">
          ${image ? `<img src="${image}" alt="${title}" loading="lazy" width="600" height="680">` : ''}
        </a>
        <div class="shadora-saved-card__body">
          <a href="${url}"><strong>${title}</strong></a>
          ${price ? `<span>${price}</span>` : ''}
        </div>
      </article>`;
  };

  const renderGrid = (selector, items, removable = false) => {
    document.querySelectorAll(selector).forEach((grid) => {
      const limit = Number(grid.dataset.limit || 0);
      const visibleItems = limit > 0 ? items.slice(0, limit) : items;
      grid.innerHTML = visibleItems.map((item) => productCardHtml(item, removable)).join('');
      grid.hidden = visibleItems.length === 0;
    });
  };

  const renderSavedProducts = () => {
    const wishlist = read(WISHLIST_KEY);
    const recent = read(RECENT_KEY);
    renderGrid('[data-shadora-wishlist-grid]', wishlist, true);
    renderGrid('[data-shadora-recent-grid]', recent, false);

    document.querySelectorAll('[data-shadora-wishlist-empty]').forEach((element) => {
      element.hidden = wishlist.length > 0;
    });
    document.querySelectorAll('[data-shadora-recent-empty]').forEach((element) => {
      element.hidden = recent.length > 0;
    });
    document.querySelectorAll('[data-shadora-wishlist-count]').forEach((element) => {
      element.textContent = wishlist.length;
      element.hidden = wishlist.length === 0;
    });
    document.querySelectorAll('[data-shadora-wishlist-label]').forEach((element) => {
      element.textContent = `${wishlist.length} ürün`;
    });
  };

  const getAccountRoot = () => document.querySelector('[data-shadora-account-root]');

  const getApiBase = () => {
    const root = getAccountRoot();
    const value = (root?.dataset.requestApiBase || '/apps/shadora-istekler').trim();
    return value.replace(/\/$/, '');
  };

  const requestApi = async (path, options = {}) => {
    const response = await fetch(`${getApiBase()}${path}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.headers || {})
      },
      ...options
    });

    const contentType = response.headers.get('content-type') || '';
    let payload = null;
    if (contentType.includes('application/json')) {
      payload = await response.json().catch(() => null);
    } else {
      const text = await response.text().catch(() => '');
      payload = text ? { message: text } : null;
    }

    if (!response.ok || payload?.ok === false) {
      const error = new Error(payload?.message || payload?.error || 'İşlem tamamlanamadı. Lütfen tekrar dene.');
      error.status = Number(payload?.status || response.status || 500);
      error.code = payload?.code || '';
      throw error;
    }
    return payload || {};
  };

  const serviceErrorMessage = (error) => {
    if (error?.status === 401) return 'Bu alanı kullanmak için müşteri hesabına giriş yapmalısın.';
    if (error?.status === 403) return 'Bu işlem için yetkin bulunmuyor.';
    if (error?.status === 404 || error?.status === 502 || error?.status === 503) {
      return error?.message || 'Hesap servisi henüz etkin değil veya geçici olarak ulaşılamıyor. Lütfen kısa süre sonra tekrar dene.';
    }
    return error?.message || 'Bir hata oluştu. Lütfen tekrar dene.';
  };

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return escapeHtml(value);
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    }).format(date);
  };

  const statusInfo = (status) => REQUEST_STATUS[status] || REQUEST_STATUS.received;

  const requestPhotoHtml = (item, admin = false) => {
    if (!item.photo_url) return '';
    const src = escapeAttribute(item.photo_url);
    const alt = escapeHtml(`${item.product_name || 'Ürün isteği'} fotoğrafı`);
    return `
      <a class="shadora-request-card__photo" href="${src}" target="_blank" rel="noopener" aria-label="Ürün fotoğrafını büyüt">
        <img src="${src}" alt="${alt}" loading="lazy" width="180" height="180">
        ${admin ? '<span>Fotoğrafı aç</span>' : ''}
      </a>`;
  };

  const customerRequestCardHtml = (item) => {
    const info = statusInfo(item.status);
    const productName = escapeHtml(item.product_name || 'Ürün isteği');
    const productUrl = item.product_url ? escapeAttribute(item.product_url) : '';
    const productLink = item.product_link ? escapeAttribute(item.product_link) : '';
    const note = item.note ? escapeHtml(item.note) : '';
    const adminNote = item.admin_note ? escapeHtml(item.admin_note) : '';

    return `
      <article class="shadora-request-card">
        <div class="shadora-request-card__header">
          <div>
            <span class="shadora-request-card__number">Talep #${escapeHtml(item.id)}</span>
            <h3>${productName}</h3>
            <time datetime="${escapeAttribute(item.created_at || '')}">${formatDate(item.created_at)}</time>
          </div>
          <span class="shadora-request-status shadora-request-status--${info.className}">${info.label}</span>
        </div>
        <div class="shadora-request-card__body">
          ${requestPhotoHtml(item)}
          <div class="shadora-request-card__details">
            ${productUrl ? `<p><strong>Gönderdiğin link:</strong> <a href="${productUrl}" target="_blank" rel="noopener">Bağlantıyı aç →</a></p>` : ''}
            ${note ? `<p><strong>Notun:</strong> ${note}</p>` : ''}
            ${adminNote ? `<p class="shadora-request-card__admin-note"><strong>Shadora notu:</strong> ${adminNote}</p>` : ''}
            ${item.status === 'published' ? `
              <div class="shadora-request-published">
                <strong>İstediğiniz ürün siteye yüklenmiştir.</strong>
                ${productLink ? `<a class="button" href="${productLink}">Ürüne Git</a>` : '<span>Ürün bağlantısı kısa süre içinde eklenecek.</span>'}
              </div>` : ''}
          </div>
        </div>
      </article>`;
  };

  const renderMyRequests = (items) => {
    const list = document.querySelector('[data-my-requests-list]');
    const empty = document.querySelector('[data-my-requests-empty]');
    if (!list) return;
    list.innerHTML = items.map(customerRequestCardHtml).join('');
    list.hidden = items.length === 0;
    if (empty) empty.hidden = items.length > 0;
  };

  const setElementState = (selector, { hidden, text } = {}) => {
    const element = document.querySelector(selector);
    if (!element) return;
    if (typeof hidden === 'boolean') element.hidden = hidden;
    if (typeof text === 'string') element.textContent = text;
  };

  const loadMyRequests = async (force = false) => {
    if (myRequestsLoaded && !force) return;
    setElementState('[data-my-requests-loading]', { hidden: false });
    setElementState('[data-my-requests-error]', { hidden: true, text: '' });
    try {
      const payload = await requestApi('/requests');
      renderMyRequests(Array.isArray(payload.requests) ? payload.requests : []);
      myRequestsLoaded = true;
    } catch (error) {
      setElementState('[data-my-requests-error]', { hidden: false, text: serviceErrorMessage(error) });
    } finally {
      setElementState('[data-my-requests-loading]', { hidden: true });
    }
  };

  const adminRequestCardHtml = (item) => {
    const info = statusInfo(item.status);
    const productName = escapeHtml(item.product_name || 'Ürün isteği');
    const productUrl = item.product_url ? escapeAttribute(item.product_url) : '';
    const productLink = escapeAttribute(item.product_link || '');
    const adminNote = escapeHtml(item.admin_note || '');
    const note = item.note ? escapeHtml(item.note) : '';
    const searchText = escapeAttribute([
      item.product_name, item.customer_name, item.customer_email, item.contact, item.status
    ].filter(Boolean).join(' ').toLocaleLowerCase('tr-TR'));

    const options = Object.entries(REQUEST_STATUS).map(([value, meta]) =>
      `<option value="${value}"${value === item.status ? ' selected' : ''}>${meta.label}</option>`
    ).join('');

    return `
      <article class="shadora-admin-request-card" data-admin-request-id="${escapeAttribute(item.id)}" data-admin-request-status="${escapeAttribute(item.status)}" data-admin-request-search-text="${searchText}">
        <div class="shadora-admin-request-card__top">
          <div>
            <span class="shadora-request-card__number">Talep #${escapeHtml(item.id)}</span>
            <h3>${productName}</h3>
            <time datetime="${escapeAttribute(item.created_at || '')}">${formatDate(item.created_at)}</time>
          </div>
          <span class="shadora-request-status shadora-request-status--${info.className}">${info.label}</span>
        </div>

        <div class="shadora-admin-request-card__content">
          ${requestPhotoHtml(item, true)}
          <div class="shadora-admin-request-card__customer">
            <h4>Müşteri bilgileri</h4>
            <dl>
              <div><dt>Ad soyad</dt><dd>${escapeHtml(item.customer_name || 'Belirtilmemiş')}</dd></div>
              <div><dt>E-posta</dt><dd><a href="mailto:${escapeAttribute(item.customer_email || '')}">${escapeHtml(item.customer_email || 'Belirtilmemiş')}</a></dd></div>
              <div><dt>Instagram / Telefon</dt><dd>${escapeHtml(item.contact || 'Belirtilmemiş')}</dd></div>
              <div><dt>Müşteri ID</dt><dd>${escapeHtml(item.customer_id || '')}</dd></div>
            </dl>
          </div>
          <div class="shadora-admin-request-card__request">
            <h4>İstek ayrıntıları</h4>
            ${productUrl ? `<p><strong>İstek ürün linki:</strong> <a href="${productUrl}" target="_blank" rel="noopener">Bağlantıyı aç →</a></p>` : '<p>Ürün bağlantısı eklenmemiş.</p>'}
            ${note ? `<p><strong>Müşteri notu:</strong> ${note}</p>` : ''}
          </div>
        </div>

        <div class="shadora-admin-request-card__editor">
          <label class="shadora-field">
            <span>Talep durumu</span>
            <select data-admin-request-status>${options}</select>
          </label>
          <label class="shadora-field shadora-field--wide">
            <span>İstek Ürün Linki</span>
            <input type="url" data-admin-product-link maxlength="1000" value="${productLink}" placeholder="https://giyim.shadora.com.tr/products/...">
          </label>
          <label class="shadora-field shadora-field--full">
            <span>Yönetici notu</span>
            <textarea data-admin-note maxlength="2000" rows="3" placeholder="Müşterinin göreceği kısa açıklama">${adminNote}</textarea>
          </label>
          <div class="shadora-admin-request-card__actions">
            <button type="button" class="button" data-save-admin-request>Değişiklikleri Kaydet</button>
            <span class="shadora-request-message" data-admin-save-message role="status" aria-live="polite"></span>
          </div>
        </div>
      </article>`;
  };

  const filterAdminRequests = () => {
    const search = (document.querySelector('[data-admin-request-search]')?.value || '').trim().toLocaleLowerCase('tr-TR');
    const status = document.querySelector('[data-admin-request-status-filter]')?.value || '';
    let visible = 0;
    document.querySelectorAll('[data-admin-request-id]').forEach((card) => {
      const matchesSearch = !search || (card.dataset.adminRequestSearchText || '').includes(search);
      const matchesStatus = !status || card.dataset.adminRequestStatus === status;
      const show = matchesSearch && matchesStatus;
      card.hidden = !show;
      if (show) visible += 1;
    });
    const empty = document.querySelector('[data-admin-requests-empty]');
    if (empty) empty.hidden = adminRequestItems.length > 0 || visible > 0;
  };

  const renderAdminRequests = (items) => {
    adminRequestItems = items;
    const list = document.querySelector('[data-admin-requests-list]');
    const empty = document.querySelector('[data-admin-requests-empty]');
    if (!list) return;
    list.innerHTML = items.map(adminRequestCardHtml).join('');
    list.hidden = items.length === 0;
    if (empty) empty.hidden = items.length > 0;
    document.querySelectorAll('[data-admin-request-count]').forEach((element) => {
      const newCount = items.filter((item) => item.status === 'received').length;
      element.textContent = newCount;
      element.hidden = newCount === 0;
    });
    filterAdminRequests();
  };

  const loadAdminRequests = async (force = false) => {
    if (adminRequestsLoaded && !force) return;
    setElementState('[data-admin-requests-loading]', { hidden: false });
    setElementState('[data-admin-requests-error]', { hidden: true, text: '' });
    try {
      const payload = await requestApi('/admin/requests');
      renderAdminRequests(Array.isArray(payload.requests) ? payload.requests : []);
      adminRequestsLoaded = true;
    } catch (error) {
      setElementState('[data-admin-requests-error]', { hidden: false, text: serviceErrorMessage(error) });
    } finally {
      setElementState('[data-admin-requests-loading]', { hidden: true });
    }
  };

  const openRequestView = (name) => {
    const root = getAccountRoot();
    if (!root) return;
    root.querySelectorAll('[data-request-view-panel]').forEach((panel) => {
      const active = panel.dataset.requestViewPanel === name;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
    root.querySelectorAll('[data-request-view]').forEach((button) => {
      const active = button.dataset.requestView === name;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    if (name === 'history') loadMyRequests();
  };

  const openTab = (name, updateHash = true) => {
    const root = getAccountRoot();
    if (!root) return;
    const target = root.querySelector(`[data-account-panel="${name}"]`);
    if (!target) return;

    root.querySelectorAll('[data-account-panel]').forEach((panel) => {
      const active = panel === target;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
    root.querySelectorAll('[data-account-tab]').forEach((button) => {
      const active = button.dataset.accountTab === name;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    if (updateHash) history.replaceState(null, '', `#${name}`);
    if (name === 'requests') loadMyRequests();
    if (name === 'all-requests') loadAdminRequests();
    if (window.innerWidth < 750) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const clearPhotoPreview = () => {
    const input = document.querySelector('[data-request-photo-input]');
    const preview = document.querySelector('[data-request-photo-preview]');
    if (requestPhotoObjectUrl) URL.revokeObjectURL(requestPhotoObjectUrl);
    requestPhotoObjectUrl = '';
    if (input) input.value = '';
    if (preview) {
      preview.hidden = true;
      const image = preview.querySelector('img');
      if (image) image.removeAttribute('src');
    }
  };

  const previewPhoto = (input) => {
    const file = input.files?.[0];
    const preview = document.querySelector('[data-request-photo-preview]');
    const message = document.querySelector('[data-request-form-message]');
    if (!file) {
      clearPhotoPreview();
      return;
    }
    if (!ALLOWED_REQUEST_PHOTO_TYPES.includes(file.type)) {
      input.value = '';
      if (message) {
        message.textContent = 'Fotoğraf JPG, PNG veya WEBP formatında olmalıdır.';
        message.className = 'shadora-request-message is-error';
      }
      return;
    }
    if (file.size > MAX_REQUEST_PHOTO_BYTES) {
      input.value = '';
      if (message) {
        message.textContent = 'Fotoğraf boyutu en fazla 5 MB olabilir.';
        message.className = 'shadora-request-message is-error';
      }
      return;
    }
    if (requestPhotoObjectUrl) URL.revokeObjectURL(requestPhotoObjectUrl);
    requestPhotoObjectUrl = URL.createObjectURL(file);
    const image = preview?.querySelector('img');
    if (image) image.src = requestPhotoObjectUrl;
    if (preview) preview.hidden = false;
    if (message) {
      message.textContent = '';
      message.className = 'shadora-request-message';
    }
  };

  const submitProductRequest = async (form) => {
    const submit = form.querySelector('[data-request-submit]');
    const message = form.querySelector('[data-request-form-message]');
    if (!form.reportValidity()) return;

    const photo = form.querySelector('[name="photo"]')?.files?.[0];
    if (photo && (!ALLOWED_REQUEST_PHOTO_TYPES.includes(photo.type) || photo.size > MAX_REQUEST_PHOTO_BYTES)) {
      if (message) {
        message.textContent = 'Fotoğraf formatını ve 5 MB boyut sınırını kontrol et.';
        message.className = 'shadora-request-message is-error';
      }
      return;
    }

    const root = getAccountRoot();
    const data = new FormData(form);
    data.append('customer_name', root?.dataset.requestCustomerName || '');
    data.append('customer_email', root?.dataset.requestCustomerEmail || '');

    if (submit) {
      submit.disabled = true;
      submit.dataset.originalText = submit.textContent;
      submit.textContent = 'Gönderiliyor…';
    }
    if (message) {
      message.textContent = '';
      message.className = 'shadora-request-message';
    }

    try {
      await requestApi('/requests', { method: 'POST', body: data });
      form.reset();
      clearPhotoPreview();
      myRequestsLoaded = false;
      if (message) {
        message.textContent = 'Ürün isteğin başarıyla alındı. Durumunu Önceki İsteklerim alanından takip edebilirsin.';
        message.className = 'shadora-request-message is-success';
      }
      await loadMyRequests(true);
      window.setTimeout(() => openRequestView('history'), 550);
    } catch (error) {
      if (message) {
        message.textContent = serviceErrorMessage(error);
        message.className = 'shadora-request-message is-error';
      }
    } finally {
      if (submit) {
        submit.disabled = false;
        submit.textContent = submit.dataset.originalText || 'İstek Gönder';
      }
    }
  };


  const updateProfileDom = (customer = {}) => {
    const root = getAccountRoot();
    const firstName = customer.first_name || '';
    const lastName = customer.last_name || '';
    const displayName = customer.display_name || [firstName, lastName].filter(Boolean).join(' ') || 'Shadora Üyesi';
    const phone = customer.phone || '';

    if (root) root.dataset.requestCustomerName = displayName;
    document.querySelectorAll('[data-customer-display-name]').forEach((element) => {
      element.textContent = displayName;
    });
    document.querySelectorAll('[data-customer-welcome-name]').forEach((element) => {
      element.textContent = firstName || displayName;
    });
    document.querySelectorAll('[data-customer-avatar]').forEach((element) => {
      element.textContent = (firstName || displayName).slice(0, 1).toUpperCase();
    });

    const profileForm = document.querySelector('[data-profile-form]');
    if (profileForm) {
      const firstNameInput = profileForm.querySelector('[name="first_name"]');
      const lastNameInput = profileForm.querySelector('[name="last_name"]');
      const phoneInput = profileForm.querySelector('[name="phone"]');
      if (firstNameInput) firstNameInput.value = firstName;
      if (lastNameInput) lastNameInput.value = lastName;
      if (phoneInput) phoneInput.value = phone;
    }
  };

  const submitProfileForm = async (form) => {
    if (!form.reportValidity()) return;
    const submit = form.querySelector('[data-profile-submit]');
    const message = form.querySelector('[data-profile-message]');
    const data = new FormData(form);
    const payload = {
      first_name: String(data.get('first_name') || '').trim(),
      last_name: String(data.get('last_name') || '').trim(),
      phone: String(data.get('phone') || '').trim()
    };

    if (submit) {
      submit.disabled = true;
      submit.dataset.originalText = submit.textContent;
      submit.textContent = 'Güncelleniyor…';
    }
    if (message) {
      message.textContent = '';
      message.className = 'shadora-request-message';
    }

    try {
      const response = await requestApi('/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      updateProfileDom(response.customer || payload);
      if (message) {
        message.textContent = 'Profil bilgilerin Shadora hesabına başarıyla kaydedildi.';
        message.className = 'shadora-request-message is-success';
      }
    } catch (error) {
      if (message) {
        message.textContent = serviceErrorMessage(error);
        message.className = 'shadora-request-message is-error';
      }
    } finally {
      if (submit) {
        submit.disabled = false;
        submit.textContent = submit.dataset.originalText || 'Bilgilerimi Güncelle';
      }
    }
  };

  const applyOrderNotePreset = (input) => {
    const form = input.closest('[data-order-note-form]');
    const textarea = form?.querySelector('[data-order-note-message]');
    if (!textarea) return;
    const presetMessage = input.dataset.presetMessage ?? ORDER_NOTE_PRESETS[input.value] ?? '';
    textarea.value = presetMessage;
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
  };

  const submitOrderNoteForm = async (form) => {
    const selected = form.querySelector('[name="note_type"]:checked');
    const textarea = form.querySelector('[data-order-note-message]');
    const submit = form.querySelector('[data-order-note-submit]');
    const status = form.querySelector('[data-order-note-message-status]');
    const orderId = form.dataset.orderId || '';

    if (!selected) {
      if (status) {
        status.textContent = 'Önce bir hazır not türü seçmelisin.';
        status.className = 'shadora-request-message is-error';
      }
      return;
    }

    const messageText = textarea?.value.trim() || '';
    if (selected.value === 'custom' && !messageText) {
      if (status) {
        status.textContent = 'Özel mesaj alanı boş bırakılamaz.';
        status.className = 'shadora-request-message is-error';
      }
      textarea?.focus();
      return;
    }

    if (submit) {
      submit.disabled = true;
      submit.dataset.originalText = submit.textContent;
      submit.textContent = 'Ekleniyor…';
    }
    if (status) {
      status.textContent = '';
      status.className = 'shadora-request-message';
    }

    try {
      const response = await requestApi(`/orders/${encodeURIComponent(orderId)}/note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note_type: selected.value,
          message: messageText
        })
      });
      form.reset();
      if (status) {
        const label = response.order?.note_label || 'Sipariş notu';
        status.textContent = `${label} notun Shadora’daki siparişine başarıyla eklendi.`;
        status.className = 'shadora-request-message is-success';
      }
    } catch (error) {
      if (status) {
        status.textContent = serviceErrorMessage(error);
        status.className = 'shadora-request-message is-error';
      }
    } finally {
      if (submit) {
        submit.disabled = false;
        submit.textContent = submit.dataset.originalText || 'Sipariş Notuna Ekle';
      }
    }
  };

  const testRequestEmail = async (button) => {
    const message = document.querySelector('[data-test-request-email-message]');
    button.disabled = true;
    const original = button.textContent;
    button.textContent = 'Gönderiliyor…';
    if (message) {
      message.textContent = '';
      message.className = 'shadora-request-message';
    }

    try {
      const response = await requestApi('/admin/mail-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'customer-account-admin' })
      });
      if (message) {
        message.textContent = response.message || 'Test e-postası gönderildi. Gelen kutusu ve spam klasörünü kontrol et.';
        message.className = 'shadora-request-message is-success';
      }
    } catch (error) {
      if (message) {
        message.textContent = serviceErrorMessage(error);
        message.className = 'shadora-request-message is-error';
      }
    } finally {
      button.disabled = false;
      button.textContent = original;
    }
  };

  const saveAdminRequest = async (button) => {
    const card = button.closest('[data-admin-request-id]');
    if (!card) return;
    const id = card.dataset.adminRequestId;
    const status = card.querySelector('[data-admin-request-status]')?.value || 'received';
    const productLink = card.querySelector('[data-admin-product-link]')?.value.trim() || '';
    const adminNote = card.querySelector('[data-admin-note]')?.value.trim() || '';
    const message = card.querySelector('[data-admin-save-message]');

    if (status === 'published' && !productLink) {
      if (message) {
        message.textContent = 'Siteye Yüklendi durumunda ürün bağlantısını eklemelisin.';
        message.className = 'shadora-request-message is-error';
      }
      return;
    }

    button.disabled = true;
    const original = button.textContent;
    button.textContent = 'Kaydediliyor…';
    if (message) {
      message.textContent = '';
      message.className = 'shadora-request-message';
    }

    try {
      await requestApi(`/admin/requests/${encodeURIComponent(id)}/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, product_link: productLink, admin_note: adminNote })
      });
      if (message) {
        message.textContent = 'Değişiklikler kaydedildi.';
        message.className = 'shadora-request-message is-success';
      }
      adminRequestsLoaded = false;
      myRequestsLoaded = false;
      await loadAdminRequests(true);
    } catch (error) {
      if (message) {
        message.textContent = serviceErrorMessage(error);
        message.className = 'shadora-request-message is-error';
      }
    } finally {
      button.disabled = false;
      button.textContent = original;
    }
  };

  document.addEventListener('submit', (event) => {
    const productRequestForm = event.target.closest('[data-product-request-form]');
    if (productRequestForm) {
      event.preventDefault();
      submitProductRequest(productRequestForm);
      return;
    }

    const profileForm = event.target.closest('[data-profile-form]');
    if (profileForm) {
      event.preventDefault();
      submitProfileForm(profileForm);
      return;
    }

    const orderNoteForm = event.target.closest('[data-order-note-form]');
    if (orderNoteForm) {
      event.preventDefault();
      submitOrderNoteForm(orderNoteForm);
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-request-photo-input]')) previewPhoto(event.target);
    if (event.target.matches('[data-admin-request-status-filter]')) filterAdminRequests();
    if (event.target.matches('[data-order-note-preset]')) applyOrderNotePreset(event.target);
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-admin-request-search]')) filterAdminRequests();
  });

  document.addEventListener('click', (event) => {
    const wishlistButton = event.target.closest('[data-shadora-wishlist-button]');
    if (wishlistButton) {
      event.preventDefault();
      event.stopPropagation();
      toggleWishlist(productFromButton(wishlistButton));
      return;
    }

    const removeButton = event.target.closest('[data-remove-wishlist]');
    if (removeButton) {
      const handle = removeButton.dataset.removeWishlist;
      write(WISHLIST_KEY, read(WISHLIST_KEY).filter((item) => item.handle !== handle));
      syncButtons();
      renderSavedProducts();
      return;
    }

    const tabButton = event.target.closest('[data-account-tab]');
    if (tabButton) {
      openTab(tabButton.dataset.accountTab);
      return;
    }

    const openTabButton = event.target.closest('[data-open-account-tab]');
    if (openTabButton) {
      openTab(openTabButton.dataset.openAccountTab);
      return;
    }

    const requestViewButton = event.target.closest('[data-request-view]');
    if (requestViewButton) {
      openRequestView(requestViewButton.dataset.requestView);
      return;
    }

    if (event.target.closest('[data-refresh-my-requests]')) {
      myRequestsLoaded = false;
      loadMyRequests(true);
      return;
    }

    if (event.target.closest('[data-refresh-admin-requests]')) {
      adminRequestsLoaded = false;
      loadAdminRequests(true);
      return;
    }

    if (event.target.closest('[data-clear-request-photo]')) {
      clearPhotoPreview();
      return;
    }

    const mailTestButton = event.target.closest('[data-test-request-email]');
    if (mailTestButton) {
      testRequestEmail(mailTestButton);
      return;
    }

    const saveAdminButton = event.target.closest('[data-save-admin-request]');
    if (saveAdminButton) {
      saveAdminRequest(saveAdminButton);
      return;
    }

    const orderButton = event.target.closest('[data-open-order]');
    if (orderButton) {
      openTab('orders');
      requestAnimationFrame(() => {
        const order = document.getElementById(orderButton.dataset.openOrder);
        if (order) {
          order.open = true;
          order.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
  });

  const init = () => {
    const currentProduct = document.querySelector('[data-shadora-current-product]');
    if (currentProduct) saveRecent(productFromButton(currentProduct));
    syncButtons();
    renderSavedProducts();

    const hash = window.location.hash.replace('#', '');
    if (hash && document.querySelector(`[data-account-panel="${hash}"]`)) {
      openTab(hash, false);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', () => {
    syncButtons();
    renderSavedProducts();
  });
})();
document.addEventListener('DOMContentLoaded', function () {
  const params = new URLSearchParams(window.location.search);
  const openOrder = params.get('open_order');

  if (!openOrder) return;

  const ordersTabButton = document.querySelector('[data-account-tab-trigger="orders"]');
  if (ordersTabButton) {
    ordersTabButton.click();
  }

  setTimeout(() => {
    const orderCards = document.querySelectorAll('.shadora-order-card');

    let matchedCard = null;

    orderCards.forEach((card) => {
      const orderName = (card.getAttribute('data-order-name') || '').trim();
      if (orderName === openOrder) {
        matchedCard = card;
      }
    });

    if (matchedCard) {
      if (matchedCard.tagName.toLowerCase() === 'details') {
        matchedCard.open = true;
      } else {
        const summary = matchedCard.querySelector('summary');
        if (summary) summary.click();
      }

      matchedCard.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, 350);
});